import React, { useState } from 'react';
import {
  Checkbox,
  DataTable,
  IconBadgeAlert,
  IconEyeOff,
  IconNotepadDocument,
  IconPencil,
  IconTrash,
  IconUser,
  Tooltip,
} from '@skf/bt-wind-rail-commonlibrary';
import style from './UserManagementDataTable.module.scss';
import { useTranslation } from 'react-i18next';
import { ExtendedUser } from '../../../../../store/users/types';
import { hasUserRolesOrGrants } from '../UserManagementList.mapper';


interface IUserManagementListTableProps {
  users?: ExtendedUser[];
  onEditUser?: (user: ExtendedUser) => void;
  onSelectUser?: (user: ExtendedUser) => void;
  onUnSelectUser?: (user: ExtendedUser) => void;
  onDeleteUser?: (user: ExtendedUser) => void;
  onDetailUser?: (user: ExtendedUser) => void;
  onPasswordReset?: (user: ExtendedUser) => void;
}

type RowType = {
  row: {
    original: ExtendedUser
  }
};

const UserManagementDataTable: React.FunctionComponent<IUserManagementListTableProps> = (
  {
    users = [],
    onEditUser = () => '',
    onSelectUser = () => '',
    onUnSelectUser = () => '',
    onDeleteUser = () => '',
    onDetailUser = () => '',
    onPasswordReset = () => ''
  }
) => {
  const [checkboxList, setCheckboxList] = useState<string[]>([]);
  const { t } = useTranslation();

  const hasCheckboxUserName = (userName: string): boolean =>
    checkboxList.some(_userName => _userName?.toLowerCase() === userName?.toLowerCase());

  const onCheckboxClick = (user: ExtendedUser) => {
    const isCheck = hasCheckboxUserName(user.userName?.toLowerCase());
    setCheckboxList(
      isCheck
        ? checkboxList.filter(_userName => user.userName?.toLowerCase() !== _userName?.toLowerCase())
        : [
          ...checkboxList,
          user.userName?.toLowerCase()
        ]
    );
    isCheck ? onUnSelectUser(user) : onSelectUser(user);
  };



  const columns: any[] = [
    {
      Header: '',
      accessor: 'Selector',
      disableSortBy: true,
      className: style.columnCheck,
      Cell: ({ row }: RowType) => {
        const user = row.original;
        return (
          <div>
            <Checkbox
              square={true}
              isChecked={hasCheckboxUserName(user.userName)}
              onClick={() => onCheckboxClick(user)}
            />
          </div>
        );
      },
    },
    {
      Header: '',
      accessor: 'Info',
      disableSortBy: true,
      className: style.columnInfo,
      Cell: ({ row }: RowType) => {
        const { isEnabled } = row.original;
        return (
          <div className={style.iconsColumns}>
            {
              !isEnabled && (
                <div
                  className={style.userDisabled}
                >
                  <Tooltip
                    className={style.tooltip}
                    position='right'
                    content={t('User_disabled')}
                  >
                    <IconEyeOff />
                  </Tooltip>
                </div>
              )
            }
            {
              (!hasUserRolesOrGrants(row.original)) && (
                <div
                  className={style.userNoRoles}
                >
                  <Tooltip
                    className={style.tooltip}
                    position='right'
                    content={t('No_roles')}
                  >
                    <IconBadgeAlert />
                  </Tooltip>
                </div>
              )
            }
          </div>
        );
      },
    },
    {
      Header: t('Username'),
      accessor: 'userName',
      maxWidth: 2,
    },
    {
      Header: t('Email'),
      accessor: 'userEmail',
    },
    {
      Header: t('Global_Id'),
      accessor: 'globalId',
      className: style.columnGlobalId,
    },
    {
      Header: 'Roles',
      className: style.columnRoles,
      accessor: (user: ExtendedUser) => user.userRoles?.join(', '),
      sortMethod: (r1: string, r2: string) => r1.localeCompare(r2)
    },
    {
      Header: '',
      accessor: 'Action',
      disableSortBy: true,
      className: style.columnActions,
      Cell: ({ row }: RowType) => {
        const { original: user } = row;
        return (
          <div className={style.iconsColumns}>

            <IconNotepadDocument
              className={!user.isSKF ? style.cursorPointer : style.resetPasswordDisabled}
              onClick={!user.isSKF ? () => onPasswordReset(user) : () => { }}
            />
            <IconPencil
              className={style.cursorPointer}
              onClick={() => onEditUser(user)}
            />
            <IconTrash
              className={style.cursorPointer}
              onClick={() => onDeleteUser(user)}
            />
            <IconUser
              className={style.cursorPointer}
              onClick={() => onDetailUser(user)}
            />
          </div>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={users}
      className={style.table}
      pagination={20}
    />
  );
};

export default UserManagementDataTable;
